import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["__scopeDialogModal"],
  _excluded2 = ["__scopeDialogModal", "asChild"],
  _excluded3 = ["__scopeDialogModal", "asChild"],
  _excluded4 = ["__scopeDialogModal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { Button } from '../../../button';
import { createDialogScope } from '../../../headless/dialog/context';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { Slot } from '../../../internal/components/Slot';
import { createContextScope } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { ModalBody, ModalClose, ModalCloseButton, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalRoot, ModalTitle, ModalTrigger } from '../Modal';
import * as styles from './DialogModal.module.css';
import { jsx as _jsx } from "react/jsx-runtime";
const ROOT_NAME = 'DialogModal';
const CONTENT_NAME = 'DialogModalContent';
const ACTIONS_NAME = 'DialogModalActions';
const ACTION_NAME = 'DialogModalAction';
const CANCEL_NAME = 'DialogModalCancel';

/**
 * создаем отдельный скоуп для DialogModal
 * чтобы избежать ошибок композиции контекстов с другими модальными окнами
 */

const [createDialogModalContext, createDialogModalScope] = createContextScope(ROOT_NAME, [createDialogScope]);
const useDialogScope = createDialogScope();
const [DialogModalContentProvider, useDialogModalContentContext] = createDialogModalContext(CONTENT_NAME);

/**
 * HOC для оборачивания компонентов Modal в скоуп DialogModal
 * при необходимости расширения функциональности, можно создать отдельные компонеты, см DialogModalContent
 */
const withDialogModalScope = Component => {
  const WrappedComponent = props => {
    const {
        __scopeDialogModal
      } = props,
      restProps = _objectWithoutProperties(props, _excluded);
    const modalScope = useDialogScope(__scopeDialogModal);
    return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, modalScope), restProps));
  };
  WrappedComponent.displayName = "Alert(".concat(Component.displayName || Component.name, ")");
  return WrappedComponent;
};

/**
 * Копоненты Modal без изменения функционала, обернутые в скоуп DialogModal
 */
export const DialogModalRoot = withDialogModalScope(ModalRoot);
export const DialogModalTrigger = withDialogModalScope(ModalTrigger);
export const DialogModalClose = withDialogModalScope(ModalClose);
export const DialogModalCloseButton = withDialogModalScope(ModalCloseButton);
export const DialogModalHeader = withDialogModalScope(ModalHeader);
export const DialogModalTitle = withDialogModalScope(ModalTitle);
export const DialogModalBody = withDialogModalScope(ModalBody);
export const DialogModalDescription = withDialogModalScope(ModalDescription);
export const DialogModalFooter = withDialogModalScope(ModalFooter);
/**
 * Компонент обертка DialogModalActions
 * используется для правильного позиционирования кнопок действия
 * @example
 * <DialogModalActions>
 *  <DialogModalCancel>Отмена</DialogModalCancel>
 *  <DialogModalAction>Принять</DialogModalAction>
 * </DialogModalActions>
 */
export const DialogModalActions = /*#__PURE__*/React.forwardRef((_ref, forwardedRef) => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: styles['actions'],
    children: children
  });
});
DialogModalActions.displayName = ACTIONS_NAME;

/**
 * Button-like компонент для "позитивного" действия в модальном окне
 */

export const DialogModalAction = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopeDialogModal,
      asChild
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  const modalScope = useDialogScope(__scopeDialogModal);
  const Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, modalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L"
    }, rest), {}, {
      ref: forwardedRef
    }))
  }));
});
DialogModalAction.displayName = ACTION_NAME;

/**
 * Button-like компонент для "негативного" действия в модальном окне
 */

export const DialogModalCancel = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopeDialogModal,
      asChild
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  const {
    cancelRef
  } = useDialogModalContentContext(CANCEL_NAME, __scopeDialogModal);
  const modalScope = useDialogScope(__scopeDialogModal);
  const ref = useComposedRefs(forwardedRef, cancelRef);
  const Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, modalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      theme: "fill_secondary",
      size: "L"
    }, rest), {}, {
      ref: ref
    }))
  }));
});
DialogModalCancel.displayName = CANCEL_NAME;
export const DialogModalContent = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopeDialogModal
    } = props,
    contentProps = _objectWithoutProperties(props, _excluded4);
  const modalScope = useDialogScope(__scopeDialogModal);
  const cancelRef = React.useRef(null);
  /**
   * При открытии фокусируемся на кнопке закрытия по умолчанию
   */
  const handleOnOpenAutoFocus = React.useMemo(() => composeEventHandlers(contentProps.onOpenAutoFocus, event => {
    var _cancelRef$current;
    event.preventDefault();
    (_cancelRef$current = cancelRef.current) === null || _cancelRef$current === void 0 || _cancelRef$current.focus({
      preventScroll: true
    });
  }), [contentProps.onOpenAutoFocus]);
  return /*#__PURE__*/_jsx(DialogModalContentProvider, {
    cancelRef: cancelRef,
    scope: __scopeDialogModal,
    children: /*#__PURE__*/_jsx(ModalContent, _objectSpread(_objectSpread(_objectSpread({}, modalScope), contentProps), {}, {
      role: "dialog",
      portalProps: modalScope,
      overlayProps: modalScope,
      onOpenAutoFocus: handleOnOpenAutoFocus,
      ref: forwardedRef
    }))
  });
});
DialogModalContent.displayName = CONTENT_NAME;
export { createDialogModalScope };